// @givmo/passkeys — the device's own word for a passkey (AS-WEB11; D-AS-21's A1, approved).
//
// The offer names what the person will actually touch: "Touch ID" on a Mac, "Face ID" on an iPhone
// or iPad, "Windows Hello" on Windows, "your phone's screen lock" on Android, and "a passkey"
// anywhere else. A browser can tell the platform but not which unlock it has, so a Mac without Touch
// ID, or an iPhone SE, reads a slightly wrong word here — the platform's own prompt then names the
// right method (the design's accepted trade, D-AS-21 question 1). Board's director pages use the
// same rule for their `methodWord`.
//
// The words keep "Touch ID", "Face ID" and "Windows Hello" on one line (a non-breaking space).

export type PasskeyDevice = 'mac' | 'iphone' | 'windows' | 'android' | 'generic';

export interface PasskeyDeviceWords {
  /** The device's own word for the method: the offer's headline noun. */
  method: string;
  /** The offer's main button. */
  setUp: string;
  /** Whether the method is a face (the button wears the face glyph) rather than a fingerprint. */
  face: boolean;
}

export const PASSKEY_DEVICE_WORDS: Readonly<Record<PasskeyDevice, PasskeyDeviceWords>> = {
  mac: { method: 'Touch ID', setUp: 'Set up Touch ID', face: false },
  iphone: { method: 'Face ID', setUp: 'Set up Face ID', face: true },
  windows: { method: 'Windows Hello', setUp: 'Set up Windows Hello', face: false },
  android: {
    method: 'your phone’s screen lock',
    setUp: 'Use your phone’s screen lock',
    face: false,
  },
  generic: { method: 'a passkey', setUp: 'Set up a passkey', face: false },
};

/** The parts of `navigator` the rule reads; injected in tests. */
export interface PasskeyDeviceScope {
  userAgent?: string;
  maxTouchPoints?: number;
}

/**
 * Which device this is, by the platform the browser reports. An iPad reporting a Mac (iPadOS's
 * desktop browsing) is told apart by its touch points.
 */
export function detectPasskeyDevice(
  scope: PasskeyDeviceScope | undefined = globalThis.navigator,
): PasskeyDevice {
  const agent = scope?.userAgent ?? '';
  if (/iPhone|iPad|iPod/.test(agent)) return 'iphone';
  if (/Android/.test(agent)) return 'android';
  if (/Macintosh|Mac OS X/.test(agent)) return (scope?.maxTouchPoints ?? 0) > 1 ? 'iphone' : 'mac';
  if (/Windows/.test(agent)) return 'windows';
  return 'generic';
}

/** The device's own word for a passkey ("Touch ID", "Face ID", …), for a sentence that names it. */
export function passkeyMethodWord(device: PasskeyDevice = detectPasskeyDevice()): string {
  return PASSKEY_DEVICE_WORDS[device].method;
}

/** The setup offer's heading and line (A1), for a host that places them as its page's heading. */
export function passkeySetupOfferWords(device: PasskeyDevice = detectPasskeyDevice()): {
  title: string;
  description: string;
} {
  return {
    title: `Sign board documents with ${PASSKEY_DEVICE_WORDS[device].method}`,
    description:
      'Signing a board document asks you to confirm it’s really you. With a passkey, that takes a second.',
  };
}
